/**
 * FOLLOWER BOOST — cinematic burst engine v4 (network cards + hero final scene)
 *
 * V4 (maquettes verrouillées « Explosion Instagram » / « Multiréseaux »): the card's own logo is
 * cloned into the overlay and pops out of the card (volume + brand glow), a neon frame and two
 * orbit swooshes wrap the card, 3D sprites leave the card on its whole perimeter (front / mid /
 * back planes) and the real service badges dock around the card corners.
 *
 * Tap/click on a network card → immediate press feedback (<100 ms) → rebound + brand halo →
 * 12–24 elements (3D sprites from Shopify Files, inline SVG fallback) fly OUT of the card across
 * the screen on 3 depth planes (back / mid / front: size, blur, speed, light trails), with the
 * REAL service tags of that network (data-fb-kinds / data-fb-tags from the catalogue) → elegant
 * dissolve → deterministic navigation to the collection. One animation per click, never a double
 * navigation, never a blocking overlay.
 *
 * Rendering: a single document-level overlay (position:fixed, pointer-events:none, z-index above
 * everything, overflow hidden ⇒ no horizontal scroll) so no section overflow:hidden / stacking
 * context can clip the particles. Coordinates are anchored to the card's viewport rect.
 *
 * Motion = WAAPI transforms/opacity only (compositor). Density adapts to the device (tier 0 =
 * reduced motion / Save-Data / ≤2 GB → native navigation, no burst; tier 1 = mobile / ≤4 cores;
 * tier 2 = desktop). Cancelled when the tab is hidden. Theme setting data-fb-burst="off".
 */
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
const MOBILE = window.matchMedia('(max-width: 749px)');
const LOW_POWER = (navigator.connection && navigator.connection.saveData) || (navigator.deviceMemory && navigator.deviceMemory <= 2);
const TIER = REDUCED.matches || LOW_POWER ? 0 : MOBILE.matches || (navigator.hardwareConcurrency || 8) <= 4 ? 1 : 2;

/* element kinds → inline SVG fallback (currentColor) */
const SVG = {
  heart: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M12 21s-7.5-4.6-9.6-9.2C.9 8.4 3 4.5 6.7 4.5c2.1 0 3.6 1.2 4.3 2.4.7-1.2 2.2-2.4 4.3-2.4 3.7 0 5.8 3.9 4.3 7.3C19.5 16.4 12 21 12 21z"/></svg>',
  thumb: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M2 10h4v11H2zM7.5 21h9.1a2 2 0 0 0 2-1.6l1.3-7A2 2 0 0 0 18 10h-5.2l.8-3.8A1.6 1.6 0 0 0 12 4.4L7.5 10z"/></svg>',
  eye: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M12 5C5.5 5 1.5 12 1.5 12S5.5 19 12 19s10.5-7 10.5-7S18.5 5 12 5zm0 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8z"/></svg>',
  user: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M12 12a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9zm0 2c-4.4 0-8 2.4-8 5.5V21h16v-1.5c0-3.1-3.6-5.5-8-5.5z"/></svg>',
  users: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M9 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm0 2c-4 0-7 2-7 4.6V21h14v-2.4C16 16 13 14 9 14zm8-2.3a3.3 3.3 0 1 0 0-6.6v6.6zM22 21v-2c0-2-1.6-3.7-4-4.3V21z"/></svg>',
  note: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M9 18.5V6l11-2.5v12a3 3 0 1 1-2-2.8V7.1L11 8.9v10.6a3 3 0 1 1-2-2.8z"/></svg>',
  play: '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="currentColor"/><path fill="#fff" d="M10 8.3v7.4l6-3.7z"/></svg>',
  bubble: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M4 4h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9l-5 4V5a1 1 0 0 1 1-1z"/></svg>',
  share: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M14 4l7 6-7 6v-4c-5 0-8 1.5-11 6 1-6 4-10 11-10z"/></svg>',
  sparkle: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M12 2l1.8 6.2L20 10l-6.2 1.8L12 18l-1.8-6.2L4 10l6.2-1.8z"/></svg>',
  star: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="m12 3 2.8 5.8 6.2.9-4.5 4.4 1.1 6.3L12 17.5l-5.6 2.9 1.1-6.3L3 9.7l6.2-.9z"/></svg>',
  live: '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3" fill="currentColor"/><path d="M7 7a7 7 0 0 0 0 10M17 7a7 7 0 0 1 0 10" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>',
  bell: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M12 22a2.5 2.5 0 0 0 2.4-2h-4.8A2.5 2.5 0 0 0 12 22zm7-6V11a7 7 0 0 0-14 0v5l-2 2v1h18v-1z"/></svg>',
  trend: '<svg viewBox="0 0 24 24"><path d="m3 17 6-6 4 4 8-8" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/><path d="M15 7h6v6" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  camera: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M9 4h6l1.5 2H20a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3.5zm3 4.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9z"/></svg>',
  film: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M3 5h18v14H3zM5 7v2h2V7zm0 4v2h2v-2zm0 4v2h2v-2zm12-8v2h2V7zm0 4v2h2v-2zm0 4v2h2v-2zM9 8.5v7l5.5-3.5z"/></svg>',
  bookmark: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M6 3h12v18l-6-4-6 4z"/></svg>',
  briefcase: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M9 4h6a2 2 0 0 1 2 2v1h4v12H3V7h4V6a2 2 0 0 1 2-2zm0 3h6V6H9z"/></svg>',
  repeat: '<svg viewBox="0 0 24 24"><path d="M4 9a5 5 0 0 1 5-5h9l-3-3M20 15a5 5 0 0 1-5 5H6l3 3" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  bolt: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M13 2 4 14h7l-1 8 9-12h-7z"/></svg>',
  plane: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M21.5 3 2.5 10.5l7 2.5 2.5 7L21.5 3zM10 13l9-7.5L12 15z"/></svg>',
  pin: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M12 2a7 7 0 0 0-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 0 0-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"/></svg>',
  dot: '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="5" fill="currentColor"/></svg>',
};

/* service kind → element kind (tags come from the catalogue; this only picks the pictogram) */
const KIND_EL = { followers: 'user', subscribers: 'user', members: 'users', listeners: 'users', likes: 'heart', reactions: 'thumb', dislikes: 'thumb', comments: 'bubble', mentions: 'bubble', shares: 'share', saves: 'bookmark', reels: 'film', stories: 'camera', live: 'live', views: 'eye', watchtime: 'play', visits: 'user', impressions: 'trend', plays: 'play', votes: 'star', reviews: 'star', generic: 'sparkle' };

/**
 * Per-network identity (reference board « bibliothèque de réseaux »): palette, ambient elements,
 * choreography pattern. Never a trademark — only the brand universe.
 *  radial  : even spokes, classic firework
 *  bloom   : spokes curve (spiral) and open like petals — Instagram, Discord
 *  pulse   : two alternating rhythms / rings — TikTok, Spotify (waves)
 *  fountain: biased upward, falls back — Twitch (chat), Telegram (planes)
 *  scatter : irregular angles, quick — Snapchat (flashes), X (posts)
 */
const PROFILES = {
  instagram: { c: ['#ff2d87', '#c13bff', '#ff7a3d', '#ffd23f'], extra: ['heart', 'film', 'eye', 'bubble', 'camera', 'sparkle'], pattern: 'bloom' },
  tiktok: { c: ['#25f4ee', '#fe2c55', '#ffffff'], extra: ['note', 'note', 'film', 'heart', 'share', 'sparkle'], pattern: 'pulse' },
  youtube: { c: ['#ff3d3d', '#ffffff', '#ff8080'], extra: ['play', 'user', 'eye', 'thumb', 'bell', 'sparkle'], pattern: 'radial' },
  snapchat: { c: ['#fffc00', '#ffffff', '#fff799'], extra: ['sparkle', 'star', 'camera', 'eye', 'bolt'], pattern: 'scatter' },
  twitch: { c: ['#9146ff', '#b58cff', '#ffffff'], extra: ['live', 'bubble', 'heart', 'users', 'sparkle'], pattern: 'fountain' },
  facebook: { c: ['#1877f2', '#4d8bff', '#ffffff'], extra: ['thumb', 'share', 'users', 'bubble', 'heart'], pattern: 'radial' },
  x: { c: ['#ffffff', '#c9ced6', '#8a9099'], extra: ['repeat', 'bubble', 'heart', 'trend', 'sparkle'], pattern: 'scatter' },
  spotify: { c: ['#1ed760', '#ffffff', '#45ff8a'], extra: ['note', 'note', 'play', 'users', 'sparkle'], pattern: 'pulse' },
  linkedin: { c: ['#0a66c2', '#5aa8ff', '#ffffff'], extra: ['briefcase', 'users', 'thumb', 'trend', 'bubble'], pattern: 'radial' },
  discord: { c: ['#5865f2', '#8b95ff', '#ffffff'], extra: ['users', 'bubble', 'live', 'sparkle', 'bolt'], pattern: 'bloom' },
  telegram: { c: ['#2aabee', '#5fc8ff', '#ffffff'], extra: ['plane', 'users', 'bubble', 'eye', 'sparkle'], pattern: 'fountain' },
  whatsapp: { c: ['#25d366', '#5cf08f', '#ffffff'], extra: ['bubble', 'users', 'share', 'sparkle', 'thumb'], pattern: 'radial' },
  vimeo: { c: ['#1ab7ea', '#6fd6ff', '#ffffff'], extra: ['play', 'eye', 'film', 'sparkle', 'heart'], pattern: 'radial' },
  pinterest: { c: ['#ff4b5c', '#ffffff'], extra: ['pin', 'bookmark', 'heart', 'sparkle'], pattern: 'bloom' },
  other: { c: ['#b7ff3c', '#ffffff', '#9be22a'], extra: ['sparkle', 'bolt', 'star', 'trend', 'heart'], pattern: 'radial' },
  /* hero final scene: every family, the brand lime leading a multi-network palette */
  hero: { c: ['#b7ff3c', '#ff2d87', '#25f4ee', '#ffd23f', '#4d8bff', '#c13bff', '#ffffff'], extra: ['heart', 'eye', 'user', 'play', 'note', 'bubble', 'share', 'users', 'star', 'sparkle', 'trend', 'film'], pattern: 'bloom' },
};

/* depth planes: back → front. dist = share of the reach, size = base scale, speed = duration factor */
const PLANES = [
  { name: 'back', dist: 0.6, size: [0.55, 0.8], blur: 1.4, alpha: 0.75, dur: 1.15, trail: false },
  { name: 'mid', dist: 0.85, size: [0.9, 1.15], blur: 0, alpha: 1, dur: 1, trail: false },
  { name: 'front', dist: 1.05, size: [1.25, 1.6], blur: 0, alpha: 1, dur: 0.92, trail: true },
];

const rand = (a, b) => a + Math.random() * (b - a);
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
let sprites = {};
const spriteCache = new Map();
let spritesLoading = null;
let layer = null;

function getLayer() {
  if (layer && layer.isConnected) return layer;
  layer = document.createElement('div');
  layer.className = 'fb-fx-layer';
  layer.setAttribute('aria-hidden', 'true');
  document.body.appendChild(layer);
  return layer;
}

function loadSprites(root = document) {
  const node = root.querySelector('[data-fb-burst-sprites]');
  if (!node) return;
  try { sprites = { ...sprites, ...JSON.parse(node.textContent || '{}') }; } catch { /* keep SVG fallback */ }
  const src = node.dataset.src;
  if (src && !spritesLoading) {
    spritesLoading = fetch(src, { credentials: 'omit' })
      .then((r) => (r.ok ? r.json() : {}))
      .then((map) => { if (map && typeof map === 'object') sprites = { ...sprites, ...map }; })
      .catch(() => { /* SVG fallback */ });
  }
}
function preload(kinds) {
  const run = () => kinds.forEach((k) => {
    const url = sprites[k];
    if (!url || spriteCache.has(k)) return;
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    spriteCache.set(k, img);
  });
  if (spritesLoading) spritesLoading.then(run); else run();
}
const spriteFailed = new Set();
function elementNode(kind, color, cls) {
  const el = document.createElement('i');
  el.className = cls;
  const url = sprites[kind];
  const svg = () => { el.innerHTML = SVG[kind] || SVG.sparkle; el.style.color = color; el.classList.remove('is-sprite'); };
  if (url && !spriteFailed.has(kind)) {
    const img = document.createElement('img');
    img.src = url;
    img.alt = '';
    img.decoding = 'async';
    img.draggable = false;
    img.onerror = () => { spriteFailed.add(kind); svg(); }; // a missing/blocked sprite never leaves a hole
    el.appendChild(img);
    el.classList.add('is-sprite');
  } else {
    svg();
  }
  return el;
}

/** angle (rad) for element i of n following the profile pattern; plane = 0..2 */
function angleFor(i, n, pattern, plane) {
  const base = (i / n) * Math.PI * 2;
  switch (pattern) {
    case 'bloom': return base + plane * 0.55 + rand(-0.12, 0.12);
    case 'pulse': return base + (i % 2 ? Math.PI / n : 0) + rand(-0.08, 0.08);
    case 'fountain': return -Math.PI / 2 + ((i / (n - 1)) - 0.5) * Math.PI * 1.35 + rand(-0.1, 0.1);
    case 'scatter': return rand(0, Math.PI * 2);
    default: return base + rand(-0.3, 0.3);
  }
}

/**
 * Play a cinematic burst anchored at a viewport point. Returns a promise resolved when the
 * elements are gone (never rejects). Used by the cards (anchor = card rect) and the hero scene.
 * opts: { cx, cy, w, h, platform, kinds, tags, intensity, speed, scene, counters }
 */
export function burstAt(opts) {
  return new Promise((resolve) => {
    const prof = PROFILES[opts.platform] || PROFILES.other;
    const tier = opts.tier ?? TIER;
    const intensity = opts.intensity || 'medium';
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const baseCount = intensity === 'low' ? 16 : intensity === 'high' ? 32 : 26;
    const count = clamp(Math.round(baseCount * (tier === 2 ? 1 : 0.8) * (opts.scene ? 1.1 : 1)), 12, 34);
    const speed = clamp(opts.speed || 1, 0.5, 1.6);
    const D = (opts.scene ? 2600 : tier === 2 ? 1900 : 1600) / speed;
    const kinds = (opts.kinds || []).map((k) => KIND_EL[k]).filter(Boolean);
    const mix = opts.scene ? [...kinds, ...prof.extra, 'sparkle', 'star'] : [...kinds, ...prof.extra];
    const reach = opts.scene
      ? Math.min(vw * 0.46, 560)
      : clamp(Math.max(opts.w, opts.h) * (tier === 2 ? 1.7 : 1.45), tier === 2 ? 260 : 170, Math.min(vw * (MOBILE.matches ? 0.62 : 0.5), 620));
    const baseSize = opts.scene ? (MOBILE.matches ? 44 : 64) : MOBILE.matches ? 58 : 78;
    const hw = (opts.w || 0) / 2;
    const hh = (opts.h || 0) / 2;
    /* distance from the anchor to the card edge along angle a (elements must leave the card) */
    const edge = (a) => (opts.scene || !hw ? 0 : Math.min(hw / Math.max(0.001, Math.abs(Math.cos(a))), hh / Math.max(0.001, Math.abs(Math.sin(a)))));
    const root = getLayer();
    const group = document.createElement('div');
    group.className = `fb-fx fb-fx--${opts.platform || 'other'}${opts.scene ? ' fb-fx--scene' : ''}`;
    group.style.setProperty('--_c', prof.c[0]);
    group.style.setProperty('--_c2', prof.c[1] || prof.c[0]);
    root.appendChild(group);
    const anims = [];
    const at = (el) => { el.style.left = `${opts.cx}px`; el.style.top = `${opts.cy}px`; group.appendChild(el); };

    /* 0 — flash + halo rings (brand colour) */
    const flash = document.createElement('span');
    flash.className = 'fb-fx__flash';
    at(flash);
    anims.push(flash.animate(
      [{ transform: 'translate(-50%,-50%) scale(0.2)', opacity: 0.95 }, { transform: 'translate(-50%,-50%) scale(1.1)', opacity: 0.35, offset: 0.35 }, { transform: 'translate(-50%,-50%) scale(2.1)', opacity: 0 }],
      { duration: 900 / speed, easing: 'cubic-bezier(.1,.8,.3,1)', fill: 'forwards' }
    ));
    for (let r = 0; r < (tier === 2 ? 3 : 2); r++) {
      const ring = document.createElement('span');
      ring.className = 'fb-fx__ring';
      ring.style.width = `${reach * 0.9}px`;
      ring.style.height = `${reach * 0.9}px`;
      at(ring);
      anims.push(ring.animate(
        [{ transform: 'translate(-50%,-50%) scale(0.25)', opacity: 0.9 }, { transform: 'translate(-50%,-50%) scale(1.4)', opacity: 0 }],
        { duration: 1100 / speed, delay: r * 170, easing: 'cubic-bezier(.1,.75,.3,1)', fill: 'forwards' }
      ));
    }

    /* 0b — card stage (V4): neon frame, orbit swooshes and the logo popping out of the card */
    if (opts.card && !opts.scene) {
      const cr = opts.card.getBoundingClientRect();
      const frame = document.createElement('span');
      frame.className = 'fb-fx__frame';
      Object.assign(frame.style, { left: `${cr.left - 6}px`, top: `${cr.top - 6}px`, width: `${cr.width + 12}px`, height: `${cr.height + 12}px` });
      group.appendChild(frame);
      anims.push(frame.animate(
        [{ transform: 'scale(0.96)', opacity: 0, easing: 'cubic-bezier(.2,.9,.3,1.2)' }, { transform: 'scale(1.035)', opacity: 1, offset: 0.12 }, { transform: 'scale(1.02)', opacity: 0.95, offset: 0.72 }, { transform: 'scale(1.06)', opacity: 0 }],
        { duration: D * 0.95, easing: 'linear', fill: 'forwards' }
      ));
      for (let k = 0; k < (tier === 2 ? 2 : 1); k++) {
        const sw = document.createElement('span');
        sw.className = 'fb-fx__swoosh';
        sw.style.width = `${cr.width * (1.5 + k * 0.25)}px`;
        sw.style.height = `${cr.height * (0.95 + k * 0.2)}px`;
        sw.style.setProperty('--_c', prof.c[k % prof.c.length]);
        sw.style.setProperty('--_c2', prof.c[(k + 1) % prof.c.length]);
        at(sw);
        const r0 = k ? 200 : -30;
        anims.push(sw.animate(
          [{ transform: `translate(-50%,-50%) rotate(${r0}deg) scale(0.6)`, opacity: 0 }, { transform: `translate(-50%,-50%) rotate(${r0 + 120}deg) scale(1)`, opacity: 1, offset: 0.3 }, { transform: `translate(-50%,-50%) rotate(${r0 + 300}deg) scale(1.12)`, opacity: 0 }],
          { duration: D * 0.9, delay: 60 + k * 90, easing: 'cubic-bezier(.3,.7,.3,1)', fill: 'forwards' }
        ));
      }
      const vis = opts.card.querySelector('.fb-network__visual, .fb-tile');
      if (vis) {
        const vr = vis.getBoundingClientRect();
        const logo = document.createElement('span');
        logo.className = 'fb-fx__logo';
        const clone = vis.cloneNode(true);
        clone.style.viewTransitionName = 'none'; // the real logo keeps the page-to-page morph
        clone.querySelectorAll('[style*="view-transition-name"]').forEach((n) => { n.style.viewTransitionName = 'none'; });
        logo.appendChild(clone);
        Object.assign(logo.style, { left: `${vr.left}px`, top: `${vr.top}px`, width: `${vr.width}px`, height: `${vr.height}px` });
        group.appendChild(logo);
        anims.push(logo.animate(
          [
            /* press → pop out of the card → breathing pulses → settles back as the page changes */
            { transform: 'translate(0,0) scale(1)', opacity: 1, easing: 'ease-out' },
            { transform: 'translate(0,-4%) scale(0.88)', opacity: 1, offset: 0.05, easing: 'cubic-bezier(.2,.9,.25,1.35)' },
            { transform: 'translate(0,-26%) scale(1.62)', opacity: 1, offset: 0.2, easing: 'ease-in-out' },
            { transform: 'translate(0,-22%) scale(1.46)', opacity: 1, offset: 0.36, easing: 'ease-in-out' },
            { transform: 'translate(0,-25%) scale(1.58)', opacity: 1, offset: 0.52, easing: 'ease-in-out' },
            { transform: 'translate(0,-22%) scale(1.48)', opacity: 1, offset: 0.68, easing: 'ease-in' },
            { transform: 'translate(0,-6%) scale(1.1)', opacity: 0.85, offset: 0.9 },
            { transform: 'translate(0,0) scale(1)', opacity: 0 },
          ],
          { duration: D, easing: 'linear', fill: 'forwards' }
        ));
      }
    }

    /* 1 — light streaks (front plane only) */
    const streaks = tier === 2 ? 8 : 5;
    for (let s = 0; s < streaks; s++) {
      const a = angleFor(s, streaks, prof.pattern === 'fountain' ? 'fountain' : 'radial', 1);
      const st = document.createElement('span');
      st.className = 'fb-fx__streak';
      st.style.width = `${reach * rand(0.55, 0.9)}px`;
      st.style.transform = `rotate(${(a * 180) / Math.PI}deg)`;
      st.style.setProperty('--_c', prof.c[s % prof.c.length]);
      at(st);
      anims.push(st.animate(
        [{ transform: `rotate(${(a * 180) / Math.PI}deg) scaleX(0)`, opacity: 0 }, { transform: `rotate(${(a * 180) / Math.PI}deg) scaleX(1)`, opacity: 0.9, offset: 0.3 }, { transform: `rotate(${(a * 180) / Math.PI}deg) scaleX(1.15)`, opacity: 0 }],
        { duration: 700 / speed, delay: 40 + s * 18, easing: 'cubic-bezier(.2,.9,.3,1)', fill: 'forwards' }
      ));
    }

    /* 2 — elements on 3 depth planes */
    for (let i = 0; i < count; i++) {
      const plane = PLANES[i % 3];
      const kind = mix[i % mix.length];
      const color = prof.c[i % prof.c.length];
      const el = elementNode(kind, color, `fb-fx__el fb-fx__el--${plane.name}`);
      const ang = angleFor(i, count, prof.pattern, i % 3);
      const dist = opts.scene || !hw ? reach * plane.dist * rand(0.8, 1.15) : edge(ang) + reach * plane.dist * rand(0.3, 0.85);
      let dx = Math.cos(ang) * dist;
      let dy = Math.sin(ang) * dist * (prof.pattern === 'fountain' ? 1 : 0.86) - (opts.h || 0) * 0.06;
      /* keep inside the viewport (no element lost off-screen, no horizontal scroll) */
      dx = clamp(dx, -opts.cx + 24, vw - opts.cx - 24);
      dy = clamp(dy, -opts.cy + 24, vh - opts.cy - 24);
      const s = rand(plane.size[0], plane.size[1]) * (baseSize / 60);
      const rot = rand(-70, 70);
      const fall = prof.pattern === 'fountain' ? 70 : 24;
      el.style.setProperty('--_blur', `${plane.blur}px`);
      el.style.zIndex = String(i % 3 + 1);
      at(el);
      const dur = D * plane.dur;
      anims.push(el.animate(
        [
          { transform: 'translate(-50%,-50%) translate(0,0) scale(0.12) rotate(0deg)', opacity: 0 },
          { transform: `translate(-50%,-50%) translate(${dx * 0.42}px,${dy * 0.42}px) scale(${s * 1.18}) rotate(${rot * 0.3}deg)`, opacity: plane.alpha, offset: 0.16 },
          { transform: `translate(-50%,-50%) translate(${dx * 0.86}px,${dy * 0.86 - 10}px) scale(${s}) rotate(${rot * 0.65}deg)`, opacity: plane.alpha, offset: 0.46 },
          { transform: `translate(-50%,-50%) translate(${dx}px,${dy - 26}px) scale(${s * 0.97}) rotate(${rot}deg)`, opacity: plane.alpha, offset: 0.78 },
          { transform: `translate(-50%,-50%) translate(${dx * 1.03}px,${dy - 26 + fall}px) scale(${s * 0.35}) rotate(${rot * 1.25}deg)`, opacity: 0 },
        ],
        { duration: dur, delay: 30 + i * 22, easing: 'cubic-bezier(.16,.9,.3,1)', fill: 'forwards' }
      ));
      /* motion trail: a lagging ghost on the front plane */
      if (plane.trail && tier === 2) {
        const ghost = elementNode(kind, color, 'fb-fx__el fb-fx__el--ghost');
        at(ghost);
        anims.push(ghost.animate(
          [
            { transform: 'translate(-50%,-50%) translate(0,0) scale(0.1)', opacity: 0 },
            { transform: `translate(-50%,-50%) translate(${dx * 0.3}px,${dy * 0.3}px) scale(${s * 0.9})`, opacity: 0.45, offset: 0.18 },
            { transform: `translate(-50%,-50%) translate(${dx * 0.8}px,${dy * 0.8}px) scale(${s * 0.7})`, opacity: 0.2, offset: 0.5 },
            { transform: `translate(-50%,-50%) translate(${dx}px,${dy}px) scale(${s * 0.3})`, opacity: 0 },
          ],
          { duration: dur, delay: 30 + i * 22 + 70, easing: 'cubic-bezier(.16,.9,.3,1)', fill: 'forwards' }
        ));
      }
    }

    /* 3 — service tags: REAL catalogue labels only (max 6), with illustrative counters in scene mode */
    const tags = (opts.tags || []).filter(Boolean).slice(0, MOBILE.matches ? 4 : 6);
    const tagKinds = opts.kinds || [];
    const tagRing = opts.scene ? reach * 0.62 : Math.max(reach * 0.72, (opts.w || 0) * 0.75);
    tags.forEach((label, i) => {
      const tag = document.createElement('b');
      tag.className = 'fb-fx__tag';
      tag.style.setProperty('--_c', prof.c[i % prof.c.length]);
      const kind = KIND_EL[tagKinds[i]] || 'sparkle';
      const counter = opts.scene && opts.counters ? `<em>${opts.counters[i % opts.counters.length]}</em>` : '';
      const ico = sprites[kind] && !spriteFailed.has(kind) ? `<img src="${sprites[kind]}" alt="">` : SVG[kind] || '';
      tag.innerHTML = `<span class="fb-fx__tag-ico">${ico}</span>${counter}${label}`;
      /* desktop: tags on an arc around the anchor; mobile: two columns (left/right), stacked rows */
      let tx;
      let ty;
      if (!opts.scene && hw) {
        /* cards: badges dock around the card (TL, TR, L, R, BL, BR) just outside its edge */
        const A = [-2.45, -0.69, Math.PI, 0, 2.45, 0.69][i % 6];
        const d = edge(A) + (MOBILE.matches ? 26 : 46);
        tx = Math.cos(A) * d;
        ty = Math.sin(A) * d * 0.92;
      } else if (MOBILE.matches) {
        const side = i % 2 ? 1 : -1;
        const row = Math.floor(i / 2);
        tx = side * Math.min(tagRing, vw * 0.27);
        ty = row * 66 - (opts.scene ? 96 : 30);
      } else {
        const spread = tags.length === 1 ? 0 : (i / (tags.length - 1)) - 0.5;
        const a = -Math.PI / 2 + spread * Math.PI * (opts.scene ? 1.6 : 1.25);
        tx = Math.cos(a) * tagRing;
        ty = Math.sin(a) * tagRing * 0.9;
      }
      const half = MOBILE.matches ? 66 : 110;
      tx = clamp(tx, -opts.cx + half, vw - opts.cx - half);
      ty = clamp(ty, -opts.cy + 30, vh - opts.cy - 30);
      at(tag);
      anims.push(tag.animate(
        [
          { transform: `translate(-50%,-50%) translate(${tx * 0.5}px,${ty * 0.5 + 14}px) scale(0.5)`, opacity: 0 },
          { transform: `translate(-50%,-50%) translate(${tx}px,${ty}px) scale(1.06)`, opacity: 1, offset: 0.22 },
          { transform: `translate(-50%,-50%) translate(${tx}px,${ty - 3}px) scale(1)`, opacity: 1, offset: 0.78 },
          { transform: `translate(-50%,-50%) translate(${tx * 1.04}px,${ty - 26}px) scale(0.86)`, opacity: 0 },
        ],
        { duration: D * 1.05, delay: 140 + i * 85, easing: 'cubic-bezier(.2,.9,.3,1)', fill: 'forwards' }
      ));
    });

    /* 4 — dust: tiny brand-coloured dots on the back plane */
    const dust = tier === 2 ? 14 : 8;
    for (let d = 0; d < dust; d++) {
      const dot = document.createElement('span');
      dot.className = 'fb-fx__dust';
      dot.style.background = prof.c[d % prof.c.length];
      const a = rand(0, Math.PI * 2);
      const dist = reach * rand(0.4, 1.2);
      const dx = clamp(Math.cos(a) * dist, -opts.cx + 8, vw - opts.cx - 8);
      const dy = clamp(Math.sin(a) * dist * 0.8, -opts.cy + 8, vh - opts.cy - 8);
      at(dot);
      anims.push(dot.animate(
        [{ transform: 'translate(-50%,-50%) translate(0,0) scale(0.3)', opacity: 0 }, { transform: `translate(-50%,-50%) translate(${dx * 0.6}px,${dy * 0.6}px) scale(1)`, opacity: 0.9, offset: 0.25 }, { transform: `translate(-50%,-50%) translate(${dx}px,${dy - 40}px) scale(0.2)`, opacity: 0 }],
        { duration: D * 0.9, delay: 20 + d * 30, easing: 'cubic-bezier(.2,.9,.3,1)', fill: 'forwards' }
      ));
    }

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      anims.forEach((a) => { try { a.cancel(); } catch { /* noop */ } });
      group.remove();
      document.removeEventListener('visibilitychange', onHide);
      resolve();
    };
    const onHide = () => { if (document.hidden) finish(); };
    document.addEventListener('visibilitychange', onHide);
    const total = D * 1.2 + count * 22 + 200;
    setTimeout(finish, opts.hold ? Math.min(total, opts.hold) : total);
    group.__finish = finish;
  });
}

/** Burst around a card element (network card). */
export function burst(card, opts = {}) {
  const r = card.getBoundingClientRect();
  return burstAt({ ...opts, card, cx: r.left + r.width / 2, cy: r.top + r.height * 0.44, w: r.width, h: r.height });
}

/* ---------- network cards ---------- */
function bindCards(root = document) {
  root.querySelectorAll('a[data-fb-net]:not([data-fb-burst-bound])').forEach((a) => {
    a.dataset.fbBurstBound = '1';
    const grid = a.closest('[data-fb-burst]');
    const kinds = (a.dataset.fbKinds || '').split(',').filter(Boolean);
    const tags = (a.dataset.fbTags || '').split('|').filter(Boolean);
    const platform = a.dataset.fbPlatform || 'other';
    const prof = PROFILES[platform] || PROFILES.other;
    const warm = () => {
      preload([...kinds.map((k) => KIND_EL[k]), ...prof.extra].filter(Boolean));
      const href = a.getAttribute('href');
      if (href && href !== '#' && !a.__prefetched) {
        a.__prefetched = true;
        const l = document.createElement('link');
        l.rel = 'prefetch';
        l.href = href;
        document.head.appendChild(l);
      }
    };
    a.addEventListener('pointerenter', warm, { once: true, passive: true });
    a.addEventListener('touchstart', warm, { once: true, passive: true });
    a.addEventListener('focus', warm, { once: true });
    /* immediate press feedback (<100 ms) */
    a.addEventListener('pointerdown', () => { if (TIER) a.classList.add('is-pressed'); }, { passive: true });
    const release = () => a.classList.remove('is-pressed');
    a.addEventListener('pointerup', release, { passive: true });
    a.addEventListener('pointercancel', release, { passive: true });
    a.addEventListener('pointerleave', release, { passive: true });

    a.addEventListener('click', (e) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button > 0) return;
      if (!grid || grid.dataset.fbBurst === 'off' || TIER === 0) return; // native navigation
      const href = a.getAttribute('href');
      if (!href || href === '#') return;
      if (a.dataset.fbBurstBusy) { e.preventDefault(); return; } // never a double navigation
      a.dataset.fbBurstBusy = '1';
      e.preventDefault();
      const intensity = grid.dataset.fbBurstIntensity || 'medium';
      a.classList.remove('is-pressed');
      a.classList.add('is-bursting');
      a.style.setProperty('--fb-burst-c', prof.c[0]);
      let navigated = false;
      const go = () => { if (navigated) return; navigated = true; window.location.assign(href); };
      /* the spectacle lasts ~1.6–2 s; navigation starts while the elements dissolve, never later than this */
      const navAt = TIER === 2 ? 1350 : 1150;
      setTimeout(go, navAt);
      burst(a, { platform, kinds, tags, intensity }).then(go);
    });
  });
}

window.addEventListener('pageshow', (e) => {
  if (e.persisted) {
    document.querySelectorAll('[data-fb-burst-busy]').forEach((a) => { delete a.dataset.fbBurstBusy; a.classList.remove('is-bursting', 'is-pressed'); });
    if (layer) layer.replaceChildren();
  }
});

const boot = () => { loadSprites(); bindCards(); };
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
document.addEventListener('shopify:section:load', (e) => { loadSprites(e.target); bindCards(e.target); });

window.FbBurst = { burst, burstAt, preload, SVG, PROFILES, KIND_EL, TIER, sprites: () => sprites };
